import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../index';
import { EmailTemplate, TimeFilterOptions } from '../types';

interface UseTemplatesOptions extends TimeFilterOptions {
  topic_id?: number;
}

interface CreateTemplateData {
  name: string;
  subject: string;
  htmlContent: string;
  textContent: string;
  topic_id?: number;
}

export const useTemplates = (options: UseTemplatesOptions = {}) => {
  const [data, setData] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (options.showAll) {
        params.showAll = 'true';
      }
      if (options.topic_id) {
        params.topic_id = options.topic_id.toString();
      }

      const result = await apiClient.get<EmailTemplate[]>('/api/email-templates', params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }, [options.showAll, options.topic_id]);

  const createTemplate = useCallback(async (templateData: CreateTemplateData) => {
    const result = await apiClient.post<EmailTemplate>('/api/email-templates', templateData);
    await fetchTemplates();
    return result;
  }, [fetchTemplates]);

  const updateTemplate = useCallback(async (id: number, templateData: CreateTemplateData) => {
    const result = await apiClient.put<EmailTemplate>(`/api/email-templates/${id}`, templateData);
    await fetchTemplates();
    return result;
  }, [fetchTemplates]);

  const deleteTemplate = useCallback(async (id: number) => {
    await apiClient.delete(`/api/email-templates/${id}`);
    await fetchTemplates();
  }, [fetchTemplates]);

  const getTemplate = useCallback(async (id: number) => {
    return apiClient.get<EmailTemplate>(`/api/email-templates/${id}`);
  }, []);

  const sendTestEmail = useCallback(async (testData: any) => {
    const { emails, email, ...restData } = testData;
    
    if (emails && Array.isArray(emails)) {
      if (emails.length === 0) {
        throw new Error('At least one email is required');
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter(e => !emailRegex.test(e));
      if (invalidEmails.length > 0) {
        throw new Error(`Invalid email format: ${invalidEmails.join(', ')}`);
      }
      
      await Promise.all(
        emails.map(emailAddr => apiClient.post('/api/email-templates/test-draft', { ...restData, email: emailAddr }))
      );
      return { message: `Test emails sent to ${emails.length} recipient(s) successfully` };
    } else {
      return apiClient.post('/api/email-templates/test-draft', testData);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    data,
    loading,
    error,
    refetch: fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplate,
    sendTestEmail,
  };
};